import { SimpleGrid, TextInput } from '@mantine/core';
import type { TextInputProps } from '@mantine/core';
import { defineMessages, useIntl } from 'react-intl';

import { AccountAddressDto } from 'api/dtos/user';

export type AddressFormValues = {
    street: string;
    buildingNumber: string;
    apartmentNumber: string;
    postalCode: string;
    city: string;
    country: string;
};

export const emptyAddressFormValues: AddressFormValues = {
    street: '',
    buildingNumber: '',
    apartmentNumber: '',
    postalCode: '',
    city: '',
    country: '',
};

export const normalizeAddressFormValues = (
    values?: (Partial<Omit<AddressFormValues, 'apartmentNumber'>> & { apartmentNumber?: string | null }) | null,
): AddressFormValues => ({
    ...emptyAddressFormValues,
    ...values,
    apartmentNumber: values?.apartmentNumber ?? '',
});

export const hasCompleteRequiredAddress = (values: AddressFormValues) => (
    values.street.trim().length > 0
    && values.buildingNumber.trim().length > 0
    && values.postalCode.trim().length > 0
    && values.city.trim().length > 0
    && values.country.trim().length > 0
);

export const toAddressDto = (values: AddressFormValues): AccountAddressDto => ({
    street: values.street.trim(),
    buildingNumber: values.buildingNumber.trim(),
    apartmentNumber: values.apartmentNumber.trim() || null,
    postalCode: values.postalCode.trim(),
    city: values.city.trim(),
    country: values.country.trim(),
});

type Props = {
    values: AddressFormValues;
    onChange: (field: keyof AddressFormValues, value: string) => void;
    fieldClassNames?: TextInputProps['classNames'];
    showValidationErrors?: boolean;
};

const AddressForm = ({
    values,
    onChange,
    fieldClassNames,
    showValidationErrors = false,
}: Props) => {
    const intl = useIntl();

    const updateField = (field: keyof AddressFormValues) => (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        onChange(field, event.currentTarget.value);
    };

    const getRequiredError = (field: keyof AddressFormValues) => (
        showValidationErrors && values[field].trim().length === 0
            ? intl.formatMessage(messages.requiredField)
            : undefined
    );

    return (
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput
                required
                classNames={fieldClassNames}
                label={intl.formatMessage(messages.street)}
                value={values.street}
                onChange={updateField('street')}
                error={getRequiredError('street')}
            />
            <TextInput
                required
                classNames={fieldClassNames}
                label={intl.formatMessage(messages.buildingNumber)}
                value={values.buildingNumber}
                onChange={updateField('buildingNumber')}
                error={getRequiredError('buildingNumber')}
            />
            <TextInput
                classNames={fieldClassNames}
                label={intl.formatMessage(messages.apartmentNumber)}
                value={values.apartmentNumber}
                onChange={updateField('apartmentNumber')}
            />
            <TextInput
                required
                classNames={fieldClassNames}
                label={intl.formatMessage(messages.postalCode)}
                value={values.postalCode}
                onChange={updateField('postalCode')}
                error={getRequiredError('postalCode')}
            />
            <TextInput
                required
                classNames={fieldClassNames}
                label={intl.formatMessage(messages.city)}
                value={values.city}
                onChange={updateField('city')}
                error={getRequiredError('city')}
            />
            <TextInput
                required
                classNames={fieldClassNames}
                label={intl.formatMessage(messages.country)}
                value={values.country}
                onChange={updateField('country')}
                error={getRequiredError('country')}
            />
        </SimpleGrid>
    );
};

const messages = defineMessages({
    street: {
        id: 'src.Components.Common.AddressForm.street',
        defaultMessage: 'Ulica',
    },
    buildingNumber: {
        id: 'src.Components.Common.AddressForm.buildingNumber',
        defaultMessage: 'Numer budynku',
    },
    apartmentNumber: {
        id: 'src.Components.Common.AddressForm.apartmentNumber',
        defaultMessage: 'Numer lokalu (opcjonalnie)',
    },
    postalCode: {
        id: 'src.Components.Common.AddressForm.postalCode',
        defaultMessage: 'Kod pocztowy',
    },
    city: {
        id: 'src.Components.Common.AddressForm.city',
        defaultMessage: 'Miasto',
    },
    country: {
        id: 'src.Components.Common.AddressForm.country',
        defaultMessage: 'Kraj',
    },
    requiredField: {
        id: 'src.Components.Common.AddressForm.requiredField',
        defaultMessage: 'To pole jest wymagane.',
    },
});

export default AddressForm;
